/**
 * Official tournament state — baseline (build-time data) + live.json overlay.
 * Everything here is the OFFICIAL state; the predictions store (Phase 2) will
 * derive from it by explicit copy, never the other way around (règle 2).
 * Pure derivation: deriveOfficialState(live) is unit-tested without DOM.
 */
import { applyResults } from "../engine/elo.ts";
import type { EloMatchUpdate } from "../engine/elo.ts";
import { rankAllGroups, rankThirds } from "../engine/standings.ts";
import type { RankedTeam, ThirdPlaceLine } from "../engine/standings.ts";
import { assignThirds } from "../engine/thirds.ts";
import { resolveBracket } from "../engine/bracket.ts";
import type { KoMatchResult, ResolvedKoMatch, Slot } from "../engine/bracket.ts";
import type { FinishedMatch, GroupMatchResult, Score, TeamCode } from "../engine/types.ts";
import type { LiveData, LiveMatchEntry } from "./liveSchema.ts";
import {
  groups,
  initialElos,
  schedule,
  buildTournamentStructure,
  type ScheduleMatch,
} from "./data.ts";

export interface MatchView {
  readonly match: ScheduleMatch;
  readonly live: LiveMatchEntry | null;
  readonly status: "scheduled" | "live" | "paused" | "finished" | "postponed";
  /** Score to display (cumulative after extra time if any). */
  readonly displayScore: Score | null;
  readonly penalties: { readonly score: readonly [number, number]; readonly winner: "home" | "away" } | null;
  readonly minute: number | null;
  /** Arrêts de jeu en cours (status.extra) — ex. minute 90 + addedTime 4 = 90+4. */
  readonly addedTime: number | null;
}

export interface GroupStanding {
  readonly group: string;
  readonly rows: readonly RankedTeam[];
  /** Teams whose line currently includes a live (provisional) score — ⏱ badge. */
  readonly provisionalTeams: ReadonlySet<TeamCode>;
  /** True once all 6 group matches are officially finished. */
  readonly complete: boolean;
}

export interface OfficialState {
  readonly live: LiveData | null;
  readonly matches: readonly MatchView[];
  readonly byId: ReadonlyMap<number, MatchView>;
  /** Officially finished matches, ascending FIFA id. */
  readonly finished: readonly FinishedMatch[];
  readonly liveNow: readonly MatchView[];
  readonly currentElos: Readonly<Record<TeamCode, number>>;
  readonly eloHistory: readonly EloMatchUpdate[];
  /** Standings INCLUDING in-progress scores (provisional, badged). */
  readonly standings: readonly GroupStanding[];
  /** Third-placed ranking over the provisional standings (top 8 highlighted). */
  readonly thirds: readonly ThirdPlaceLine[];
  /** Official R32 teams for slots whose source groups are complete. */
  readonly r32Teams: Readonly<Record<string, readonly [TeamCode | null, TeamCode | null]>>;
  /** True when all 12 groups are complete (thirds assignment official). */
  readonly groupsAllComplete: boolean;
  readonly bracket: Readonly<Record<string, ResolvedKoMatch>>;
}

const structure = buildTournamentStructure();

function endOfPlayScore(entry: LiveMatchEntry): Score | null {
  if (entry.extraTime) return entry.extraTime.score;
  if (entry.score) return entry.score.ft;
  return null;
}

/**
 * Arrêts de jeu d'un match TERMINÉ : la source ne donne ni le temps additionnel total
 * ni d'évènement « coup de sifflet final ». On l'estime en SOMMANT, par période (1re
 * mi-temps ≤45, 2nde ≤90, prolongation ET1 ≤105, ET2 sinon), le dépassement le plus
 * grand observé sur un évènement de cette période. Deux encodages couverts :
 *  - API-Football : `elapsed` borné à la fin de période (45/90/105/120) + dépassement
 *    dans `extra` → on lit `extra`.
 *  - sources de repli (communauté/openfootball) : minute BRUTE, sans `extra` → si la
 *    minute dépasse la fin réglementaire FINALE (90, ou 120 en prolongation), ce
 *    dépassement EST forcément du temps additionnel (rien ne se joue après) → on le
 *    compte. (Un 47' brut reste ambigu — 45+2 ou 47e de jeu — donc ignoré.)
 * Minorant : ne capte une période que si un évènement y a eu lieu en temps additionnel.
 */
function endStoppage(entry: LiveMatchEntry, wentExtraTime: boolean): number | null {
  if (!entry.events?.length) return null;
  const finalEnd = wentExtraTime ? 120 : 90;
  const period = (m: number): number => (m <= 45 ? 45 : m <= 90 ? 90 : m <= 105 ? 105 : 120);
  const maxByPeriod = new Map<number, number>();
  for (const e of entry.events) {
    const m = e.minute ?? 0;
    let p: number;
    let over: number;
    if (e.extra && e.extra > 0) {
      p = period(m);
      over = e.extra;
    } else if (m > finalEnd) {
      p = finalEnd; // dépassement de la dernière période réglementaire = temps add. certain
      over = m - finalEnd;
    } else continue;
    maxByPeriod.set(p, Math.max(maxByPeriod.get(p) ?? 0, over));
  }
  let total = 0;
  for (const v of maxByPeriod.values()) total += v;
  return total > 0 ? total : null;
}

/** Vrai si le match est allé en prolongation (buts en prolongation ou tirs au but). */
function wentToExtraTime(entry: LiveMatchEntry): boolean {
  if (entry.penalties) return true;
  const et = entry.extraTime?.score;
  return !!et && (et[0] > 0 || et[1] > 0);
}

/** Paramètres de modèle (utilisés par le mode Simulation pour rejouer avec
 *  d'autres Elo / tirages). Par défaut : Elo réels + seed officiel. */
export interface DeriveOptions {
  readonly elos?: Readonly<Record<TeamCode, number>>;
  readonly drawSeed?: string;
}

export function deriveOfficialState(live: LiveData | null, opts: DeriveOptions = {}): OfficialState {
  const baseElos = opts.elos ?? initialElos;
  const seed = opts.drawSeed ?? "wc26-officiel";
  const liveById = new Map<number, LiveMatchEntry>();
  if (live) for (const m of live.matches) liveById.set(m.id, m);

  const matches: MatchView[] = schedule.map((match) => {
    const entry = liveById.get(match.id) ?? null;
    const status = entry?.status ?? "scheduled";
    return {
      match,
      live: entry,
      status,
      displayScore: entry ? endOfPlayScore(entry) : null,
      penalties: entry?.penalties ?? null,
      minute: entry?.minute ?? null,
      // en direct : status.extra ; terminé : estimé depuis les évènements (par période).
      addedTime: entry?.addedTime ?? (status === "finished" && entry ? endStoppage(entry, wentToExtraTime(entry)) : null),
    };
  });
  const byId = new Map(matches.map((v) => [v.match.id, v]));
  const liveNow = matches.filter((v) => v.status === "live" || v.status === "paused");

  // --- finished matches -> Elo ---------------------------------------------
  const finished: FinishedMatch[] = matches
    .filter((v) => v.status === "finished" && v.displayScore)
    .map((v) => ({
      id: v.match.id,
      home: v.match.home,
      away: v.match.away,
      score: v.displayScore as Score,
      penalties: v.penalties !== null,
      hostCountry: v.match.hostCountry,
    }))
    // knockout placeholders are not teams: only group matches and KO matches
    // whose live entry exists make sense — KO teams are resolved below; for
    // Elo, KO matches enter once their REAL teams are known via the bracket.
    .sort((a, b) => a.id - b.id);

  // KO matches: home/away in schedule.json are placeholders ("1A", "W73").
  // Resolve actual teams from the bracket BEFORE computing Elo on them.
  const groupFinished = finished.filter((m) => m.id <= 72);

  // --- group standings (provisional: finished + live scores) ---------------
  const resultsByGroup: Record<string, GroupMatchResult[]> = {};
  const provisionalByGroup: Record<string, Set<TeamCode>> = {};
  const finishedCountByGroup: Record<string, number> = {};
  for (const v of matches) {
    if (v.match.stage !== "group" || v.match.group === null) continue;
    const g = v.match.group;
    const isFinished = v.status === "finished" && v.displayScore !== null;
    const isLive = (v.status === "live" || v.status === "paused") && v.live?.score !== undefined;
    if (!isFinished && !isLive) continue;
    // Match en cours : on badge ⏱ tout de suite, mais on n'intègre PAS un 0-0
    // de coup d'envoi (minute 0) au classement — sinon le groupe se réordonne
    // dès la 1re minute sur un 0-0 fictif (départage aléatoire seedé).
    if (isLive) {
      (provisionalByGroup[g] ??= new Set()).add(v.match.home);
      (provisionalByGroup[g] ??= new Set()).add(v.match.away);
    }
    const liveFt = v.live?.score?.ft;
    const meaningful =
      isFinished || (!!liveFt && ((v.minute ?? 0) > 0 || liveFt[0] !== 0 || liveFt[1] !== 0));
    if (!meaningful) continue;
    const score: Score = isFinished ? v.displayScore : (liveFt as Score);
    (resultsByGroup[g] ??= []).push({ home: v.match.home, away: v.match.away, score });
    if (isFinished) finishedCountByGroup[g] = (finishedCountByGroup[g] ?? 0) + 1;
  }

  // Elo: group matches + resolved KO matches (teams known once propagated)
  // First pass on groups only, then KO below after bracket resolution.
  const groupElo = applyResults(baseElos, groupFinished);

  const ranks = rankAllGroups(groups, resultsByGroup, {
    elos: groupElo.elos,
    drawSeed: seed,
  });
  const standings: GroupStanding[] = Object.keys(groups)
    .sort()
    .map((g) => ({
      group: g,
      rows: ranks[g] ?? [],
      provisionalTeams: provisionalByGroup[g] ?? new Set(),
      complete: (finishedCountByGroup[g] ?? 0) === 6,
    }));

  const thirds = rankThirds(
    standings
      .map((s) => ({ group: s.group, line: s.rows[2] }))
      .filter((t): t is { group: string; line: RankedTeam } => t.line !== undefined && t.line.played > 0),
    { elos: groupElo.elos, drawSeed: `${seed}-3es` },
  );

  // --- official R32 (only slots whose source group(s) are complete) --------
  const completeGroups = new Set(standings.filter((s) => s.complete).map((s) => s.group));
  const groupsAllComplete = completeGroups.size === 12;
  const thirdsAssignment = groupsAllComplete
    ? assignThirds(
        thirds.slice(0, 8).map((t) => t.group),
        structure.thirdsTable,
      ).bySlot
    : null;

  const r32Teams: Record<string, readonly [TeamCode | null, TeamCode | null]> = {};
  for (const [nodeId, [srcA, srcB]] of Object.entries(structure.r32Sources)) {
    const resolveToken = (token: string): TeamCode | null => {
      if (token.startsWith("3")) {
        if (!thirdsAssignment) return null;
        const slot = token.includes(":") ? token.slice(token.indexOf(":") + 1) : nodeId;
        const group = thirdsAssignment[slot];
        return group !== undefined ? (ranks[group]?.[2]?.team ?? null) : null;
      }
      const rank = Number(token[0]);
      const group = token.slice(1);
      if (!completeGroups.has(group)) return null;
      return ranks[group]?.[rank - 1]?.team ?? null;
    };
    r32Teams[nodeId] = [resolveToken(srcA), resolveToken(srcB)];
  }

  // --- knockout propagation -------------------------------------------------
  const koResults: Record<string, KoMatchResult> = {};
  for (const v of matches) {
    if (v.match.stage === "group" || v.status !== "finished" || !v.displayScore) continue;
    const winnerSlot: Slot | undefined = v.penalties
      ? v.penalties.winner === "home"
        ? "a"
        : "b"
      : undefined;
    koResults[`M${v.match.id}`] = {
      score: v.displayScore,
      ...(winnerSlot ? { penaltiesWinner: winnerSlot } : {}),
    };
  }
  const r32Filled = Object.fromEntries(
    Object.entries(r32Teams).filter(([, [a, b]]) => a !== null && b !== null),
  ) as Record<string, readonly [TeamCode, TeamCode]>;
  const bracket = resolveBracket(structure.nodes, r32Filled, koResults);

  // --- Elo over KO matches with known teams ---------------------------------
  const koFinished: FinishedMatch[] = [];
  for (const v of matches) {
    if (v.match.stage === "group" || v.status !== "finished" || !v.displayScore) continue;
    const resolved = bracket[`M${v.match.id}`];
    if (!resolved || resolved.a === null || resolved.b === null) continue;
    koFinished.push({
      id: v.match.id,
      home: resolved.a,
      away: resolved.b,
      score: v.displayScore,
      penalties: v.penalties !== null,
      hostCountry: v.match.hostCountry,
    });
  }
  const fullElo = applyResults(baseElos, [...groupFinished, ...koFinished]);

  return {
    live,
    matches,
    byId,
    finished: [...groupFinished, ...koFinished].sort((a, b) => a.id - b.id),
    liveNow,
    currentElos: fullElo.elos,
    eloHistory: fullElo.history,
    standings,
    thirds,
    r32Teams,
    groupsAllComplete,
    bracket,
  };
}
